import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { CONFIG } from "../services/config";

interface Report {
  jobId: string;
  downloadUrl: string;
  status: "notStarted" | "processing" | "completed" | "failed";
  errorMessage?: string;
}

const USE_MOCK = false;

export default function useSatisfactionSurvey() {
  const [report, setReport] = useState<Report>({
    jobId: "",
    downloadUrl: "",
    status: "notStarted",
  });

  const [callbackFunction, setCallbackFunction] = useState<
    (report: Report) => void
  >(() => () => {});
  const [callbackButtonLoading, setCallbackButtonLoading] = useState<
    (value: boolean) => void
  >(() => () => {});

  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);

  const verifyReportStatus = async (jobId: string) => {
    const header = {
      headers: {
        ...tokenHeaders.headers,
        "Country-Code": countryCode,
      },
    };

    const result = await axios.get(
      `${CONFIG.API_URL}evaluations/reports/jobs/${jobId}`,
      header,
    );

    return result.data;
  };

  const startReportStatusLoopCheck = async (
    jobId: string,
    downloadUrl: string,
  ) => {
    const result = await verifyReportStatus(jobId);

    if (result.status === "PROCESSING") {
      setTimeout(() => {
        setReport({
          jobId,
          downloadUrl,
          status: "processing",
        });
      }, 10000);
    } else if (result.status === "COMPLETED") {
      const completedReport: Report = {
        jobId,
        downloadUrl: result.downloadUrl,
        status: "completed",
      };

      setReport(completedReport);
    } else if (result.status === "FAILED") {
      const failedReport: Report = {
        jobId,
        downloadUrl: "",
        status: "failed",
        errorMessage: result.errorMessage,
      };

      setReport(failedReport);
    }
  };

  useEffect(() => {
    const { jobId, downloadUrl, status } = report;
    if (status !== "notStarted") {
      if (status === "processing") {
        startReportStatusLoopCheck(jobId, downloadUrl);
      }

      if (status === "completed") {
        callbackFunction?.(report);
        callbackButtonLoading?.(false);
      }

      if (status === "failed") {
        callbackFunction?.(report);
        callbackButtonLoading?.(false);
      }
    }
  }, [report]);

  const sendReportGeneration = async (
    body: {
      initialDate: string;
      finalDate: string;
    },
    callbackAppRatingReport: (report: Report) => void,
    setButtonLoading: (value: boolean) => void,
  ) => {
    const header = {
      headers: {
        ...tokenHeaders.headers,
        "Country-Code": countryCode,
      },
    };
    // mock
    if (USE_MOCK) {
      setCallbackFunction(() => callbackAppRatingReport);
      setCallbackButtonLoading(() => setButtonLoading);

      setReport({
        jobId: "mock-job-id",
        downloadUrl: "",
        status: "processing",
      });

      setTimeout(() => {
        const mockResult: Report = {
          jobId: "mock-job-id",
          downloadUrl: "https://mock-download/report.xlsx",
          status: "completed",
        };

        setReport(mockResult);
      }, 2000);

      return;
    } // mock

    return axios
      .post(
        `${CONFIG.API_URL}surveys/reports/export`,
        {
          startDate: body.initialDate,
          endDate: body.finalDate,
        },
        header,
      )
      .then(({ data }) => {
        const newReport: Report = {
          jobId: data.jobId,
          downloadUrl: "",
          status: "processing",
        };

        setCallbackFunction(() => callbackAppRatingReport);
        setCallbackButtonLoading(() => setButtonLoading);
        setReport(newReport);
      })
      .catch(() => {
        setCallbackFunction(() => callbackAppRatingReport);
        setCallbackButtonLoading(() => setButtonLoading);
        const failedReport: Report = {
          jobId: "",
          downloadUrl: "",
          status: "failed",
        };
        setReport(failedReport);
      });
  };

  return { sendReportGeneration, report };
}
